import React, { useEffect, useMemo, useState } from "react";
import AppShell from "@/components/layout/AppShell";
import { api } from "@/lib/api";
import { useI18n } from "@/contexts/I18nContext";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, Plus, X } from "lucide-react";

const PLATFORM_COLORS = {
  instagram: "#E1306C", tiktok: "#000000", twitter: "#1DA1F2",
  linkedin: "#0A66C2", youtube: "#FF0000", facebook: "#1877F2",
};

function getMonthGrid(year, month) {
  const first = new Date(year, month, 1);
  const startDow = first.getDay(); // 0 Sun
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < startDow; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(new Date(year, month, d));
  while (cells.length % 7 !== 0) cells.push(null);
  return cells;
}

export default function CalendarPage() {
  const { lang } = useI18n();
  const now = new Date();
  const [cursor, setCursor] = useState({ y: now.getFullYear(), m: now.getMonth() });
  const [posts, setPosts] = useState([]);
  const [modalDate, setModalDate] = useState(null);
  const [form, setForm] = useState({ title: "", content: "", platforms: [], time: "10:00" });

  const load = async () => {
    const { data } = await api.get("/posts");
    setPosts(data.posts.filter((p) => p.scheduled_at));
  };
  useEffect(() => { load(); }, []);

  const cells = useMemo(() => getMonthGrid(cursor.y, cursor.m), [cursor]);
  const monthLabel = new Date(cursor.y, cursor.m).toLocaleString(lang === "ar" ? "ar" : "en", { month: "long", year: "numeric" });

  const postsByDate = useMemo(() => {
    const map = {};
    posts.forEach((p) => {
      const d = new Date(p.scheduled_at);
      const key = `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
      map[key] = map[key] || [];
      map[key].push(p);
    });
    return map;
  }, [posts]);

  const openNew = (date) => { setModalDate(date); setForm({ title: "", content: "", platforms: [], time: "10:00" }); };

  const save = async () => {
    if (!form.title) { toast.error("Title required"); return; }
    const [h, min] = form.time.split(":");
    const scheduled = new Date(modalDate);
    scheduled.setHours(Number(h), Number(min), 0, 0);
    try {
      await api.post("/posts", { ...form, hashtags: "", media_urls: [], status: "scheduled", scheduled_at: scheduled.toISOString() });
      toast.success(lang === "ar" ? "تم جدولة المنشور" : "Post scheduled");
      setModalDate(null); load();
    } catch { toast.error("Failed"); }
  };

  const days = lang === "ar" ? ["أحد", "إثن", "ثلا", "أرب", "خمي", "جمع", "سبت"] : ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

  return (
    <AppShell>
      <div className="mb-6 flex items-end justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-display text-3xl">{lang === "ar" ? "تقويم النشر" : "Content Calendar"}</h1>
          <p className="text-muted-foreground text-sm mt-1">{lang === "ar" ? "خطط شهرك ورتّب منشوراتك." : "Plan your month and schedule your posts."}</p>
        </div>
        <div className="flex items-center gap-2">
          <button data-testid="cal-prev-btn" onClick={() => setCursor(({ y, m }) => m === 0 ? { y: y - 1, m: 11 } : { y, m: m - 1 })} className="p-2 border border-border rounded-sm hover:bg-muted"><ChevronLeft className="w-4 h-4" /></button>
          <div className="font-display text-lg px-4 capitalize min-w-[180px] text-center" data-testid="cal-label">{monthLabel}</div>
          <button data-testid="cal-next-btn" onClick={() => setCursor(({ y, m }) => m === 11 ? { y: y + 1, m: 0 } : { y, m: m + 1 })} className="p-2 border border-border rounded-sm hover:bg-muted"><ChevronRight className="w-4 h-4" /></button>
        </div>
      </div>

      <div className="nx-card overflow-hidden" data-testid="calendar-grid">
        <div className="grid grid-cols-7 border-b border-border/60 bg-muted/30">
          {days.map((d) => <div key={d} className="p-3 text-xs uppercase tracking-widest text-muted-foreground text-center">{d}</div>)}
        </div>
        <div className="grid grid-cols-7">
          {cells.map((date, i) => {
            const key = date ? `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}` : null;
            const dayPosts = key ? (postsByDate[key] || []) : [];
            const isToday = date && date.toDateString() === new Date().toDateString();
            return (
              <div key={i} data-testid={date ? `cal-day-${date.getDate()}` : ""}
                className={`min-h-[110px] p-2 border-e border-b border-border/40 group ${!date ? "bg-muted/20" : "hover:bg-muted/30 cursor-pointer"} ${isToday ? "bg-primary/5" : ""}`}
                onClick={() => date && openNew(date)}>
                {date && (
                  <>
                    <div className="flex items-center justify-between">
                      <span className={`text-xs ${isToday ? "font-semibold text-primary" : "text-muted-foreground"}`}>{date.getDate()}</span>
                      <Plus className="w-3 h-3 text-muted-foreground opacity-0 group-hover:opacity-100" />
                    </div>
                    <div className="mt-1 space-y-0.5">
                      {dayPosts.slice(0, 3).map((p, j) => (
                        <div key={j} className="text-[10px] px-1.5 py-0.5 rounded-sm truncate text-white"
                          style={{ background: PLATFORM_COLORS[p.platforms?.[0]] || "#002FA7" }}>
                          {new Date(p.scheduled_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })} · {p.title}
                        </div>
                      ))}
                      {dayPosts.length > 3 && <div className="text-[10px] text-muted-foreground">+{dayPosts.length - 3}</div>}
                    </div>
                  </>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {modalDate && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={() => setModalDate(null)}>
          <div className="nx-card p-6 max-w-md w-full" onClick={(e) => e.stopPropagation()} data-testid="cal-modal">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-display text-xl">{lang === "ar" ? "جدولة منشور" : "Schedule post"}</h3>
              <button data-testid="cal-modal-close" onClick={() => setModalDate(null)} className="text-muted-foreground hover:text-foreground"><X className="w-4 h-4" /></button>
            </div>
            <div className="text-xs text-muted-foreground mb-3">{modalDate.toDateString()}</div>
            <input data-testid="cal-post-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
              placeholder={lang === "ar" ? "عنوان" : "Title"}
              className="w-full bg-background border border-border rounded-sm px-3 py-2 outline-none focus:border-primary mb-3" />
            <textarea data-testid="cal-post-content" value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })}
              rows={3} placeholder={lang === "ar" ? "المحتوى" : "Content"}
              className="w-full bg-background border border-border rounded-sm px-3 py-2 outline-none focus:border-primary mb-3" />
            <div className="flex flex-wrap gap-1 mb-3">
              {Object.keys(PLATFORM_COLORS).map((p) => (
                <button key={p} data-testid={`cal-platform-${p}`} onClick={() => setForm((f) => ({ ...f, platforms: f.platforms.includes(p) ? f.platforms.filter((x) => x !== p) : [...f.platforms, p] }))}
                  className={`text-xs px-2 py-1 rounded-sm border transition ${form.platforms.includes(p) ? "text-white border-transparent" : "border-border"}`}
                  style={form.platforms.includes(p) ? { background: PLATFORM_COLORS[p] } : {}}>{p}</button>
              ))}
            </div>
            <input type="time" data-testid="cal-post-time" value={form.time} onChange={(e) => setForm({ ...form, time: e.target.value })}
              className="w-full bg-background border border-border rounded-sm px-3 py-2 outline-none focus:border-primary mb-4" />
            <button data-testid="cal-save-btn" onClick={save} className="nx-btn-primary w-full">{lang === "ar" ? "جدولة" : "Schedule"}</button>
          </div>
        </div>
      )}
    </AppShell>
  );
}
